import { useCallback, useEffect, useState, useRef } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowsClockwise, CloudArrowDown, DownloadSimple, Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ALL = "__ALL__";
const PAGE_SIZE = 50;

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const DEFAULT_LOOKBACK_DAYS = 90;
const fmtDt = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
};
const iso2ToFlag = (iso2) => {
  if (!iso2 || iso2.length !== 2) return "";
  const base = 0x1F1E6;
  return String.fromCodePoint(
    base + iso2.toUpperCase().charCodeAt(0) - 65,
    base + iso2.toUpperCase().charCodeAt(1) - 65,
  );
};

const STATE_STYLES = {
  APPROVED: "bg-emerald-100 text-emerald-700",
  PENDING: "bg-amber-100 text-amber-700",
  REVERSED: "bg-rose-100 text-rose-700",
  approved: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  hold: "bg-indigo-100 text-indigo-700",
  rejected: "bg-rose-100 text-rose-700",
};

export default function AdminSiteplug() {
  const [health, setHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(true);

  const [startDate, setStartDate] = useState(daysAgo(DEFAULT_LOOKBACK_DAYS));
  const [endDate, setEndDate] = useState(today());
  const [stateFilter, setStateFilter] = useState("");
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, payout: 0, gross_sale_amount: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [syncingCatalog, setSyncingCatalog] = useState(false);
  const [syncingReport, setSyncingReport] = useState(false);
  const didFetch = useRef(false);

  const fetchHealth = useCallback(async () => {
    setLoadingHealth(true);
    try {
      const { data } = await API.get("/admin/siteplug/health");
      setHealth(data);
      if (data?.needs_state_backfill > 0) {
        await API.post("/admin/siteplug/backfill-states");
        const { data: refreshed } = await API.get("/admin/siteplug/health");
        setHealth(refreshed);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load Siteplug health");
    } finally {
      setLoadingHealth(false);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
        network: "siteplug",
        // Network page = reconciliation view: list $0 conversions too.
        include_zero: "1",
      });
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      if (stateFilter) params.set("state", stateFilter);
      const { data } = await API.get(`/admin/actions?${params.toString()}`);
      let items = data.items || [];
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        items = items.filter((r) =>
          [r.action_id, r.campaign_name, r.advertiser_name, r.sub_id1, r.sub_id2]
            .some((v) => String(v || "").toLowerCase().includes(q)),
        );
      }
      setRows(items);
      setTotal(data.total || 0);
      setStats(data.stats || { total: 0, payout: 0, gross_sale_amount: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load Siteplug conversions");
    } finally {
      setLoading(false);
    }
  }, [page, startDate, endDate, stateFilter, search]);

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    fetchHealth();
  }, [fetchHealth]);
  useEffect(() => {
    const t = setTimeout(fetchRows, 250);
    return () => clearTimeout(t);
  }, [fetchRows]);

  const handleSyncCatalog = async () => {
    setSyncingCatalog(true);
    try {
      const { data } = await API.post("/admin/siteplug/sync-catalog");
      toast.success(
        `Catalog synced · ${data?.live_count ?? 0} live · ${data?.upserted ?? data?.brands ?? 0} brands`,
      );
      fetchHealth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Catalog sync failed");
    } finally {
      setSyncingCatalog(false);
    }
  };

  const handleSyncReport = async () => {
    if (!startDate || !endDate) {
      toast.error("Pick start and end dates");
      return;
    }
    setSyncingReport(true);
    try {
      const { data } = await API.post("/admin/siteplug/sync-report", null, {
        params: { start_date: startDate, end_date: endDate },
      });
      if (data?.ok === false) {
        toast.error(data.reason || data.error || "Report sync failed");
      } else {
        toast.success(
          `Pulled ${data?.rows_seen ?? 0} conversions · ${data?.upserted ?? 0} new · ${data?.modified ?? 0} updated`,
        );
        setPage(1);
        fetchRows();
        fetchHealth();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Report sync failed");
    } finally {
      setSyncingReport(false);
    }
  };

  const downloadCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { data } = await API.get("/admin/actions", {
        params: {
          network: "siteplug",
          include_zero: 1,
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
          ...(stateFilter ? { state: stateFilter } : {}),
        },
      });
      const all = Array.isArray(data) ? data : (data.items || []);
      const headers = [
        "event_date", "action_id", "campaign", "advertiser", "publisher", "country",
        "sub_id1", "sub_id2", "state", "amount", "payout", "currency",
      ];
      const lines = [headers.join(",")];
      for (const r of all) {
        lines.push([
          r.event_date, r.action_id, r.campaign_name, r.advertiser_name,
          r.publisher_email, r.brand_country, r.sub_id1, r.sub_id2, r.state || r.status,
          r.amount, r.payout, r.currency,
        ].map((v) => {
          const s = v == null ? "" : String(v);
          return s.includes(",") ? `"${s.replace(/"/g, '""')}"` : s;
        }).join(","));
      }
      const blob = new Blob([lines.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `siteplug-conversions-${startDate}-to-${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const configured = health?.configured;

  return (
    <div className="p-8 space-y-6" data-testid="admin-siteplug-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 font-bold tracking-[0.2em] uppercase">NETWORK</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Siteplug</h1>
          <p className="text-sm text-slate-500 mt-1">
            Affise CPA feed — catalog sync + conversion report pull into <code className="bg-slate-100 px-1 rounded text-[11px]">actions</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={downloadCsv}
            disabled={exporting || total === 0}
            data-testid="siteplug-export"
          >
            <DownloadSimple size={14} className={`mr-1.5 ${exporting ? "animate-pulse" : ""}`} />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { fetchHealth(); fetchRows(); }} data-testid="siteplug-refresh">
            <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {!loadingHealth && !configured ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="siteplug-not-configured">
          <strong>Siteplug API not configured.</strong> Add <code className="bg-amber-100 px-1 rounded">SITEPLUG_API_KEY</code> and{" "}
          <code className="bg-amber-100 px-1 rounded">SITEPLUG_API_DOMAIN</code> to <code className="bg-amber-100 px-1 rounded">backend/.env</code>, then restart the backend.
        </div>
      ) : null}

      {/* Health + sync */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3 text-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Catalog</div>
          <div className="font-heading text-xl font-bold text-slate-900 mt-1">
            {(health?.brands_active ?? 0).toLocaleString()} active
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">
            {(health?.brands_total ?? 0).toLocaleString()} total brands
          </div>
          <Button
            variant="outline" size="sm" className="mt-3"
            onClick={handleSyncCatalog}
            disabled={syncingCatalog || !configured}
            data-testid="siteplug-sync-catalog"
          >
            <ArrowsClockwise size={14} className="mr-1.5" />
            {syncingCatalog ? "Syncing…" : "Sync catalog"}
          </Button>
        </div>
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3 text-sm lg:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">
            Pull conversions from Siteplug API
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Start</Label>
              <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} data-testid="siteplug-start" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">End</Label>
              <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} data-testid="siteplug-end" />
            </div>
            <Button
              onClick={handleSyncReport}
              disabled={syncingReport || !configured}
              className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white"
              data-testid="siteplug-sync-report"
            >
              <CloudArrowDown size={14} className="mr-1.5" />
              {syncingReport ? "Pulling…" : "Sync report"}
            </Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-2">
            Last report sync: {health?.last_report?.finished_at ? fmtDt(health.last_report.finished_at) : "never"} ·{" "}
            {(health?.actions_total ?? 0).toLocaleString()} conversions in DB
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="siteplug-filters">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Status</Label>
          <Select value={stateFilter || ALL} onValueChange={(v) => { setStateFilter(v === ALL ? "" : v); setPage(1); }}>
            <SelectTrigger data-testid="siteplug-state"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="PENDING">Pending / Hold</SelectItem>
              <SelectItem value="REVERSED">Declined</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Search (this page)</Label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="action ID / brand / sub_id"
            data-testid="siteplug-search"
          />
        </div>
      </div>

      {/* Period totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm" data-testid="siteplug-rollup">
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Conversions (period)</div>
          <div className="font-heading text-xl font-bold text-slate-900 mt-1">{(stats.total ?? total).toLocaleString()}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">{startDate} → {endDate}</div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Commission (period)</div>
          <div className="font-heading text-xl font-bold text-[#002FA7] mt-1">${Number(stats.payout || 0).toFixed(2)}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">USD net (excludes reversed)</div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sales (period)</div>
          <div className="font-heading text-xl font-bold text-slate-900 mt-1">${Number(stats.gross_sale_amount || 0).toFixed(2)}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">USD gross</div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Date</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Brand</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Action ID</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sub IDs</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Publisher</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Country</th>
              <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sales</th>
              <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Commission</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400 text-sm">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-slate-500 text-sm">
                  {configured
                    ? "No conversions for this window — try Sync report above."
                    : "Configure Siteplug API keys first."}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.action_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`siteplug-row-${r.action_id}`}>
                <td className="px-3 py-2 text-[11px] text-slate-700 whitespace-nowrap">{fmtDt(r.event_date)}</td>
                <td className="px-3 py-2 text-[11px] font-semibold text-slate-800">{r.campaign_name || r.advertiser_name || "—"}</td>
                <td className="px-3 py-2 text-[11px] font-mono text-slate-600">{r.action_id}</td>
                <td className="px-3 py-2 text-[11px] font-mono text-slate-600">
                  {[r.sub_id1, r.sub_id2].filter(Boolean).join(" · ") || "—"}
                </td>
                <td className="px-3 py-2 text-[11px] text-slate-700">{r.publisher_email || "—"}</td>
                <td className="px-3 py-2 text-[11px] text-slate-600">
                  {r.brand_country ? (
                    <span className="inline-flex items-center gap-1">
                      <span>{iso2ToFlag(r.brand_country)}</span>
                      <span className="font-mono">{r.brand_country}</span>
                    </span>
                  ) : "—"}
                </td>
                <td className="px-3 py-2 text-[11px] font-mono text-right">${Number(r.amount || 0).toFixed(2)}</td>
                <td className="px-3 py-2 text-[11px] font-mono text-right text-[#002FA7] font-bold">${Number(r.payout || 0).toFixed(2)}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${STATE_STYLES[r.state || r.status] || "bg-slate-100 text-slate-600"}`}>
                    {r.state || r.status || "—"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <div>
          {total.toLocaleString()} conversion{total === 1 ? "" : "s"} · page <span className="font-mono font-bold">{page}</span> / <span className="font-mono">{totalPages}</span>
        </div>
        <div className="flex gap-1.5">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Prev</Button>
          <Button variant="outline" size="sm" disabled={loading || page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
